import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Key } from "lucide-react";

const PasswordResetPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  // Cooldown timer
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = setInterval(() => setCooldown((c) => c - 1), 1000);
    return () => clearInterval(timer);
  }, [cooldown]);

  const handleRequestReset = useCallback(async (e) => {
    if (e) e.preventDefault();
    if (cooldown > 0) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/request-password-reset`, { email });
      if (res.data?.message?.includes("wait")) {
        toast.info(res.data.message);
      } else {
        toast.success("Reset code sent! Check your email.");
        setStep(2);
      }
      setCooldown(120);
    } catch (error) {
      toast.error("Failed to send reset code");
    } finally {
      setLoading(false);
    }
  }, [email, cooldown]);

  const handleResendCode = async () => {
    if (cooldown > 0) return;
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-password-reset`, { email });
      toast.success("New reset code sent! Check your email.");
      setCooldown(120);
      setResetToken("");
    } catch (error) {
      toast.error("Failed to resend code");
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();

    if (newPassword !== confirmPassword) {
      toast.error("Passwords do not match");
      return;
    }

    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken.toUpperCase().trim(),
        new_password: newPassword
      });
      toast.success("Password reset successfully! You can now login.");
      navigate('/auth');
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reset password");
    } finally {
      setLoading(false);
    }
  };

  const formatCooldown = (seconds) => {
    const m = Math.floor(seconds / 60);
    const s = seconds % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <button
          onClick={() => navigate('/auth')}
          className="mb-6 flex items-center gap-2 text-sm font-bold hover:underline"
          data-testid="back-login-btn"
        >
          <ArrowLeft size={16} />
          BACK TO LOGIN
        </button>

        <div className="brutal-card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-primary border-2 border-black flex items-center justify-center">
              <Key className="w-6 h-6 text-white" strokeWidth={3} />
            </div>
            <h1 className="text-2xl font-syne font-bold">
              RESET PASSWORD
            </h1>
          </div>

          {step === 1 ? (
            <form onSubmit={handleRequestReset} className="space-y-4">
              <p className="text-sm mb-4">
                Enter your email address and we&apos;ll send you an 8-character reset code.
              </p>
              <div>
                <label className="block text-sm font-bold mb-2">Email</label>
                <input
                  type="email"
                  className="brutal-input w-full"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                />
              </div>
              <button
                type="submit"
                className="brutal-btn-primary w-full"
                disabled={loading || cooldown > 0}
                data-testid="request-reset-btn"
              >
                {loading ? 'SENDING...' : cooldown > 0 ? `RESEND IN ${formatCooldown(cooldown)}` : 'SEND RESET CODE'}
              </button>
            </form>
          ) : (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <p className="text-sm mb-4">
                Enter the 8-character code from your email and your new password.
              </p>
              <div>
                <label className="block text-sm font-bold mb-2">Reset Code</label>
                <input
                  type="text"
                  className="brutal-input w-full font-mono text-center text-lg tracking-widest uppercase"
                  value={resetToken}
                  onChange={(e) => setResetToken(e.target.value.toUpperCase().slice(0, 8))}
                  required
                  maxLength={8}
                  data-testid="token-input"
                  placeholder="XXXXXXXX"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">New Password</label>
                <input
                  type="password"
                  className="brutal-input w-full"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="new-password-input"
                />
              </div>
              <div>
                <label className="block text-sm font-bold mb-2">Confirm Password</label>
                <input
                  type="password"
                  className="brutal-input w-full"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  minLength={6}
                  data-testid="confirm-password-input"
                />
              </div>
              <button
                type="submit"
                className="brutal-btn-primary w-full"
                disabled={loading}
                data-testid="reset-password-btn"
              >
                {loading ? 'RESETTING...' : 'RESET PASSWORD'}
              </button>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={cooldown > 0 || loading}
                className="w-full py-2 text-sm font-bold text-purple-700 hover:underline disabled:text-gray-400 disabled:no-underline"
                data-testid="resend-code-btn"
              >
                {cooldown > 0 ? `Resend code in ${formatCooldown(cooldown)}` : 'Resend Code'}
              </button>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="brutal-btn-outline w-full"
                data-testid="back-step-btn"
              >
                BACK
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default PasswordResetPage;
